import { Component, OnInit } from '@angular/core';
import { MessageBoxComponent } from '@common/popups/message-box/message-box.component';
import { LayoutService } from '@common/services/layout.service';
import { ModalService } from '@common/services/modal.service';
import { DatePickerHandler } from '@common/UI/date-picker/date-picker.component';
import { SortableExtractor } from '@common/UI/sortable/sortable.component';
import { Filterable } from '@common/utilities/Filterable';
import { commit, main } from '@core/operators';
import { DataService, DateRange, ProductUnit, GoodReportDetail } from '@core/services/data.service';
import { PartnersService } from '@core/services/partners.service';
import { SynchronizationService } from '@core/services/synchronization.service';
import { Multilingual } from '@core/utilities/multilingual';
import { Storage } from '@core/utilities/storage';
import { map } from 'rxjs/operators';
import { Report } from '../common/report';
import { ExportsViewerComponent } from './exports-viewer/exports-viewer.component';
import { ReturnsViewerComponent } from './returns-viewer/returns-viewer.component';
import { Observable } from 'rxjs';


@Component({
  templateUrl: './inventory-report-by-customers.component.html',
  styleUrls: ['./inventory-report-by-customers.component.scss']
})
export class InventoryReportByCustomersComponent implements OnInit {

  public get mode(): 'quantity' | 'worth' {
    return Storage.get(location.pathname, 'quantity');
  }

  public set mode(value: 'quantity' | 'worth') {
    Storage.set(location.pathname, value);
  }

  public paths: Report[] = [];
  public report: Report;

  public range: { name: string, range: DateRange } = null;

  constructor(private ds: DataService, private layout: LayoutService, private sync: SynchronizationService, private modal: ModalService, private partners: PartnersService) {
  }

  public get loading(): boolean {
    return this.range && !this.sync.ready(InventoryReportByCustomersComponent);
  }

  public total(type: 'Count' | 'Quantity' | 'QuoteQuantity' | 'Discount', ...reports: GoodReportDetail[]): number {
    return reports.sum(i => i['Length' + type] + i['Weight' + type]);
  }

  public length(type: 'Count' | 'Quantity' | 'QuoteQuantity' | 'Discount', ...reports: GoodReportDetail[]): number {
    return reports.sum(i => i['Length' + type]);
  }

  public weight(type: 'Count' | 'Quantity' | 'QuoteQuantity' | 'Discount', ...reports: GoodReportDetail[]): number {
    return reports.sum(i => i['Weight' + type]);
  }

  public goods = new Filterable<Report>({
    extractor: i => [i.Path.join('.')],
    grouper: i => [
      { name: 'Theo trạng thái nhập', option: i.Imports.LengthCount > 0 ? 'Có nhập' : 'Không nhập' },
      { name: 'Theo trạng thái bán', option: i.Exports.LengthCount > 0 ? 'Có bán' : 'Không bán' },
      { name: 'Theo trạng thái trả', option: i.Returns.LengthCount > 0 ? 'Có trả' : 'Không trả' },
      { name: 'Theo trạng thái mất', option: i.CompanyLosses.LengthCount > 0 ? 'Có mất' : 'Không mất' },
      { name: 'Theo trạng thái tồn', option: 1 > 0 ? 'Có tồn' : 'Hết hàng' },
    ]
  });

  public selectMode() {
    this.modal.show(MessageBoxComponent, {
      state: {
        title: Multilingual.getText('Chọn hình thức hiển thị'),
        message: Multilingual.getText('Chọn một trong các hình thức hiển thị dữ liệu dưới đây') + ':',
        buttons: [
          { name: Multilingual.getText('Hiển thị theo số lượng hàng'), class: this.mode == 'quantity' ? '' : 'bg-primary text-white', click: () => this.mode = 'quantity', icon: this.mode == 'quantity' ? 'fa fa-check' : 'fa fa-boxes' },
          { name: Multilingual.getText('Hiện thị theo giá trị hàng'), class: this.mode == 'worth' ? '' : 'bg-primary text-white', click: () => this.mode = 'worth', icon: this.mode == 'worth' ? 'fa fa-check' : 'fa fa-coins' }
        ]
      }
    });
  }

  private synchronize(observable: boolean): Observable<boolean> {    

    if (observable) {

      this.sync.notify(InventoryReportByCustomersComponent, false);

      return this.ds.reports('goods-by-customers', { range: this.range.range }).pipe(map(data => {

        this.sync.notify(InventoryReportByCustomersComponent, true);

        if (data && data.Goods) {

          Report.reset(Multilingual.getText('Tất cả khách hàng'));
          data.Goods.forEach(i => Report.add(i));

          this.paths = [];
          this.open(Report.root);

          return true;
        }

        return false;
      }));
    }
    else {

      this.sync.with(InventoryReportByCustomersComponent).repeat(() => this.ds.reports('goods-by-customers', { range: this.range.range }).pipe(map(data => data && data.Goods
        ? commit<boolean>(() => {
          Report.reset(Multilingual.getText('Tất cả khách hàng'));
          data.Goods.forEach(i => Report.add(i));

          this.paths = [];
          this.open(Report.root);

        }).return(true)
        : false)));      

      return null;
    }
  }

  public extractor(field: string): SortableExtractor<Report> {
    return {
      extract: i => {
        switch (field) {
          case 'last-of-path': return i.Path.last();
          case 'initial': return i.base('initial', 'CNY');
          case 'import': return i.base('import', 'CNY') - i.base('provider', 'CNY');
          case 'return': return i.quote('return', 'VND');
          case 'return-quantity': return i.Returns.LengthQuantity + i.Returns.WeightQuantity;
          case 'discount': return i.quote('discount', 'VND');
          case 'discount-quantity': return i.Exports.LengthDiscount + i.Exports.WeightDiscount;
          case 'lost': return i.quote('lost', 'VND');
          case 'export': return i.quote('export', 'VND');
          case 'export-quantity': return i.Exports.LengthQuantity + i.Exports.WeightQuantity;
          case 'exchange': return i.exchange;
          case 'gross': return i.gross('total', 'VND');
          case '%': return i.base('profitable', 'CNY') != 0 ? i.gross('total', 'CNY') * 100 / i.base('profitable', 'CNY') : 0
        }
      }
    };
  }

  public handler: DatePickerHandler = {
    change: (info: { name: string, range: DateRange }) => {

      this.range = info;

      return this.synchronize(true);
    }
  }

  public get primaryLabel(): string {
    switch (this.paths.indexOf(this.report)) {
      case 0: return Multilingual.getText('Khách hàng');
      case 1: return Multilingual.getText('Mã hàng');
      case 2: return Multilingual.getText('Mã màu');
    }
  }


  public statistics(field: 'left' | 'gross-value' | 'gross-percentage' | 'discount' | 'exchange' | 'return' | 'export' | 'import'): number {
    switch (field) {
      case 'gross-value': return this.goods.matches.sum(i => i.gross('total', 'VND'));
      case 'gross-percentage': return this.goods.matches.sum(i => i.gross('total', 'CNY')) / this.goods.matches.sum(i => i.base('profitable', 'CNY'));
      case 'import': return this.goods.matches.sum(i => this.mode == 'worth' ? i.Imports.QuoteValue : i.Imports.LengthQuantity - i.ProviderLosses.LengthQuantity);
      case 'exchange':

        let cny = this.goods.matches.sum(i => i.gross('total', 'CNY'));
        return cny != 0 ? this.statistics('gross-value') / cny : 0;

      default: return this.goods.matches.sum(i => i.quote(field as any, 'VND'));
    }
  }

  public sum(type: string, field: string = 'Length') {
    switch (type) {
      case 'left': return this.goods.matches.sum(i => i.left(field.toLowerCase() as any));
      default: return this.goods.matches.sum(i => i[type][field]);
    }

  }

  public open(report: Report) {

    if (report.children.length < 1) return;

    if (!this.paths.contains(report)) this.paths.push(report);
    this.paths = this.paths.take(this.paths.indexOf(report) + 1);
    this.report = report;

    this.goods.source = report.children;
    this.goods.search = '';
  }

  public openStatistics(report: Report, mode: "exports" | "returns") {

    this.sync.with(InventoryReportByCustomersComponent).commit(done => {

      switch (mode) {
        case "exports":
          this.ds
            .reports("goods-by-exports", { range: this.range.range, customerId: this.partners.get(report.Path[0]).ID, sku: report.Path.length > 1 ? report.Path[1] : undefined, color: report.Path.length > 2 ? report.Path[2] : undefined })
            .subscribe(reports => {
              done();
              this.modal.show(ExportsViewerComponent, { state: { reports: reports.Goods, range: this.range, customer: report.Path[0], SKU: report.Path.length > 1 ? report.Path[1] : undefined, color: report.Path.length > 2 ? report.Path[2] : undefined } }).content.changed.subscribe(invoice => this.synchronize(false));
            });

          break;

        case "returns":
          this.ds
            .reports("goods-by-returns", { range: this.range.range, customerId: this.partners.get(report.Path[0]).ID, sku: report.Path.length > 1 ? report.Path[1] : undefined, color: report.Path.length > 2 ? report.Path[2] : undefined })
            .subscribe(reports => {
              done();
              this.modal.show(ReturnsViewerComponent, { state: { reports: reports.Goods, range: this.range, customer: report.Path[0], SKU: report.Path.length > 1 ? report.Path[1] : undefined, color: report.Path.length > 2 ? report.Path[2] : undefined } }).content.changed.subscribe(invoice => this.synchronize(false))
            });

          break;

        default:

          done();
          break;
      }

    });
  }

  ngOnInit(): void {
    main(() => this.layout.title = Multilingual.getText('Báo cáo hàng hóa theo khách hàng'));
  }
}
